'use client';

import { c as _c } from "react/compiler-runtime";
import { Bot, Copy, ExternalLink, FileCode2, GitCompareArrows, MessageSquarePlus, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BetterTooltip } from '@/components/ui/tooltip';
type PrV2FileHeaderActionsMenuPermissions = {
  isReadOnly: boolean;
  canComment: boolean;
};
type PrV2FileHeaderActionsMenuLinks = {
  oldGithubFileUrl: string | null;
  newGithubFileUrl: string | null;
};
type PrV2FileHeaderActionsMenuView = {
  canToggleCodeView: boolean;
  isCodeView: boolean;
  codeViewToggleLabel: string;
};
type PrV2FileHeaderActionsMenuActions = {
  onAddFileToAiChat: () => void;
  onToggleFileDraft: () => void;
  onToggleCodeView: () => void;
  onCopyFilePath: () => void;
  onCopyFileDiff: () => void;
  onOpenOldFileInGitHub: () => void;
  onOpenNewFileInGitHub: () => void;
};
type PrV2FileHeaderActionsMenuProps = {
  filename: string;
  isFileLevelDraftOpen: boolean;
  permissions: PrV2FileHeaderActionsMenuPermissions;
  links: PrV2FileHeaderActionsMenuLinks;
  view: PrV2FileHeaderActionsMenuView;
  actions: PrV2FileHeaderActionsMenuActions;
};
export function PrV2FileHeaderActionsMenu(t0) {
  const $ = _c(39);
  const {
    filename,
    isFileLevelDraftOpen,
    permissions,
    links,
    view,
    actions
  } = t0;
  const t1 = `More actions for ${filename}`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <MoreHorizontal className="size-3.5" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== t1) {
    t3 = <BetterTooltip content="More actions" side="top"><DropdownMenuTrigger asChild={true}><Button type="button" variant="ghost" size="icon" className="size-6 shrink-0 rounded-sm text-zinc-500 hover:text-zinc-300 [&_svg]:size-3.5" aria-label={t1} data-pr-v2-file-header-control="true">{t2}</Button></DropdownMenuTrigger></BetterTooltip>;
    $[1] = t1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== actions.onAddFileToAiChat || $[4] !== actions.onToggleFileDraft || $[5] !== isFileLevelDraftOpen || $[6] !== permissions.canComment || $[7] !== permissions.isReadOnly) {
    t4 = !permissions.isReadOnly ? <><DropdownMenuItem onSelect={actions.onAddFileToAiChat}><Bot className="size-3.5 text-zinc-400" />Add to AI chat</DropdownMenuItem><DropdownMenuItem onSelect={actions.onToggleFileDraft} disabled={!permissions.canComment}><MessageSquarePlus className="size-3.5 text-zinc-400" />{isFileLevelDraftOpen ? "Close file comment" : "Add file comment"}{!isFileLevelDraftOpen ? <DropdownMenuShortcut>C</DropdownMenuShortcut> : null}</DropdownMenuItem></> : null;
    $[3] = actions.onAddFileToAiChat;
    $[4] = actions.onToggleFileDraft;
    $[5] = isFileLevelDraftOpen;
    $[6] = permissions.canComment;
    $[7] = permissions.isReadOnly;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== actions.onToggleCodeView || $[10] !== view.canToggleCodeView || $[11] !== view.codeViewToggleLabel || $[12] !== view.isCodeView) {
    t5 = view.canToggleCodeView ? <DropdownMenuItem onSelect={actions.onToggleCodeView}>{view.isCodeView ? <GitCompareArrows className="size-3.5 text-zinc-400" /> : <FileCode2 className="size-3.5 text-zinc-400" />}{view.isCodeView ? "View diff" : view.codeViewToggleLabel}</DropdownMenuItem> : null;
    $[9] = actions.onToggleCodeView;
    $[10] = view.canToggleCodeView;
    $[11] = view.codeViewToggleLabel;
    $[12] = view.isCodeView;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Copy className="size-3.5 text-zinc-400" />;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== actions.onCopyFilePath) {
    t7 = <DropdownMenuItem onSelect={actions.onCopyFilePath}>{t6}Copy file path</DropdownMenuItem>;
    $[15] = actions.onCopyFilePath;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Copy className="size-3.5 text-zinc-400" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== actions.onCopyFileDiff) {
    t9 = <DropdownMenuItem onSelect={actions.onCopyFileDiff}>{t8}Copy entire diff contents</DropdownMenuItem>;
    $[18] = actions.onCopyFileDiff;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuSeparator />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = !links.oldGithubFileUrl;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ExternalLink className="size-3.5 text-zinc-400" />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== actions.onOpenOldFileInGitHub || $[23] !== t11) {
    t13 = <DropdownMenuItem onSelect={actions.onOpenOldFileInGitHub} disabled={t11}>{t12}View old file in GitHub</DropdownMenuItem>;
    $[22] = actions.onOpenOldFileInGitHub;
    $[23] = t11;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !links.newGithubFileUrl;
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ExternalLink className="size-3.5 text-zinc-400" />;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== actions.onOpenNewFileInGitHub || $[27] !== t14) {
    t16 = <DropdownMenuItem onSelect={actions.onOpenNewFileInGitHub} disabled={t14}>{t15}View new file in GitHub</DropdownMenuItem>;
    $[26] = actions.onOpenNewFileInGitHub;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t13 || $[30] !== t16 || $[31] !== t4 || $[32] !== t5 || $[33] !== t7 || $[34] !== t9) {
    t17 = <DropdownMenuContent align="end" className="min-w-[220px]" data-pr-v2-file-header-control="true">{t4}{t5}{t7}{t9}{t10}{t13}{t16}</DropdownMenuContent>;
    $[29] = t13;
    $[30] = t16;
    $[31] = t4;
    $[32] = t5;
    $[33] = t7;
    $[34] = t9;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17 || $[37] !== t3) {
    t18 = <DropdownMenu modal={false}>{t3}{t17}</DropdownMenu>;
    $[36] = t17;
    $[37] = t3;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
}